import { ArrowDropDownFilled } from "@clockwise/icons";
import { Combobox, TextInput, useCombobox } from "@mantine/core";
import classNames from "classnames";
import React, { useState } from "react";
import { getMantineSize } from "../components/DateTime/mantine.util";
import { INPUT_HEIGHT_CLASS, INPUT_TEXT_CLASS } from "../constants/classes";
import { Size } from "../constants/types";

export interface AutocompleteProps {
  data: (string | { value: string; label: string })[];
  disabled?: boolean;
  error?: string;
  fullWidth?: boolean;
  label?: string;
  onChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
  size?: Size;
  withinPortal?: boolean;
  withIcon?: boolean;
  maxDropdownHeight?: string;
  "aria-label"?: string;
  [key: string]: unknown;
}

export const Autocomplete = ({
  data,
  disabled,
  error,
  fullWidth = false,
  label,
  onChange,
  onInputChange,
  placeholder,
  value = "",
  size = "medium",
  withinPortal = true,
  withIcon = true,
  maxDropdownHeight = "220px",
  "aria-label": ariaLabel,
  ...props
}: AutocompleteProps) => {
  const [search, setSearch] = useState(value);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Handle mouse down to select all text on first click
  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!hasBeenClicked) {
      // Only select all text on the first click
      e.preventDefault();
      e.currentTarget.select();
      setHasBeenClicked(true);
    }
  };

  // Reset the clicked state when the input loses focus
  const handleBlur = () => {
    setHasBeenClicked(false);
  };

  // Convert data to a format we can use for filtering
  const formattedData = data.map((item) =>
    typeof item === "string" ? { value: item, label: item } : item,
  );

  // Filter options based on search input
  const shouldFilterOptions = !formattedData.some((item) => item.value === search);
  const filteredOptions = shouldFilterOptions
    ? formattedData.filter((item) => item.label.toLowerCase().includes(search.toLowerCase().trim()))
    : formattedData;

  const options = filteredOptions.map((item) => (
    <Combobox.Option value={item.value} key={item.value}>
      {item.label}
    </Combobox.Option>
  ));

  const mantineSize = getMantineSize(size);

  return (
    <Combobox
      onOptionSubmit={(optionValue) => {
        setSearch(optionValue);
        onChange?.(optionValue);
        combobox.closeDropdown();
      }}
      withinPortal={withinPortal}
      store={combobox}
      classNames={{
        dropdown: "cw-text-sm",
        options: "cw-w-full",
        option: "cw-text-sm",
      }}
      {...props}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          placeholder={placeholder}
          value={search}
          size={mantineSize}
          aria-label={ariaLabel}
          classNames={{
            root: classNames({ "cw-w-full": fullWidth }),
            input: classNames(INPUT_HEIGHT_CLASS[size], INPUT_TEXT_CLASS[size]),
          }}
          onChange={(event) => {
            const newValue = event.currentTarget.value;
            setSearch(newValue);
            onInputChange?.(newValue);
            combobox.updateSelectedOptionIndex();
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={handleBlur}
          onMouseDown={handleMouseDown}
          error={error}
          disabled={disabled}
          rightSectionPointerEvents="none"
          rightSection={
            withIcon ? (
              <ArrowDropDownFilled
                className="cw-w-4 cw-h-4"
                style={{
                  color: disabled ? "var(--mantine-color-gray-4)" : "var(--mantine-color-gray-7)",
                }}
              />
            ) : null
          }
        />
      </Combobox.Target>

      {options.length > 0 && (
        <Combobox.Dropdown>
          <Combobox.Options style={{ maxHeight: maxDropdownHeight, overflowY: "auto" }}>
            {options}
          </Combobox.Options>
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
};
